import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { catchError, map, switchMap, switchMapTo, withLatestFrom } from 'rxjs/operators';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { AddExtensionGroupComponent } from '../../add-extension-group/containers/add-extension-group.component';
import { ExtensionGroupOrderingOptions, ExtensionGroupRegisterOptions } from '../models/extension-register-options';
import {
   ExtensionGroupRegisterListItem,
   ExtensionGroupRegisterResults,
} from '../models/extension-register-results.model';
import { ExtensionGroupsFilterOption } from '../models/extensions-group-filter-option';
import {
   GetExtensionGroups,
   GetExtensionGroups_extensionGroupsConnection_items,
} from '../queries/generated/GetExtensionGroups';
import { ExtensionGroupRegisterApiService } from '../services/extension-group-register-api.service';
import { createExtensionGroup, extensionGroupRegisterAdapterActions } from './extension-group-register.actions';
import {
   ExtensionGroupRegisterState,
   selectEndCursor,
   selectOrderingOptions,
   selectQuickFilters,
} from './extension-group-register.reducer';
import * as ExtensionGroupRegisterSelectors from './extension-group-register.selectors';

@Injectable()
export class ExtensionGroupRegisterEffects {
   addExtensionGroup$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(createExtensionGroup),
            switchMap((createExtensionGroupTypeOption) => {
               const dialogRef = this.dialogService.openFormComponent(AddExtensionGroupComponent, {
                  data: createExtensionGroupTypeOption,
                  disableClose: true,
                  panelClass: ['add-dialog-panel', 'dialog-container'],
               });
               this.dialogId = dialogRef.id;
               return dialogRef.afterClosed().pipe(switchMapTo(EMPTY));
            })
         );
      },
      { dispatch: false }
   );

   loadExtensionGroupList$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(extensionGroupRegisterAdapterActions.pageEnter),
         withLatestFrom(
            this.store.pipe(select(ExtensionGroupRegisterSelectors.selectFilters)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([, filters, quickFilter, orderingOptions]) => {
            return this.loadNewData(filters, quickFilter, orderingOptions);
         })
      );
   });

   loadExtensionGroupPage$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(extensionGroupRegisterAdapterActions.loadPage),
         withLatestFrom(
            this.store.pipe(select(ExtensionGroupRegisterSelectors.selectFilters)),
            this.store.pipe(select(selectEndCursor)),
            this.store.pipe(select(selectOrderingOptions)),
            this.store.pipe(select(selectQuickFilters))
         ),
         switchMap(([action, filters, endCursor, orderingOptions, quickFilters]) => {
            return this.loadNewPage(endCursor, filters, quickFilters, orderingOptions);
         })
      );
   });

   quickFilterChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(extensionGroupRegisterAdapterActions.quickFilterChanged),
         withLatestFrom(
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(ExtensionGroupRegisterSelectors.selectFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, quickFilters, filters, orderingOptions]) => {
            return this.loadNewData(filters, quickFilters, orderingOptions);
         })
      );
   });

   orderingOptionsChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(extensionGroupRegisterAdapterActions.orderingOptionsChanged),
         withLatestFrom(
            this.store.pipe(select(ExtensionGroupRegisterSelectors.selectFilters)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, filters, quickfilters, orderingOptions]) => {
            return this.loadNewData(filters, quickfilters, orderingOptions);
         })
      );
   });

   dialogId: string;

   constructor(
      private actions$: Actions,
      private store: Store<ExtensionGroupRegisterState>,
      private dialogService: LcsDialogService,
      private extensionGroupRegisterApiService: ExtensionGroupRegisterApiService
   ) {}

   private convertGetExtensionGroupsResultToLoadSuccessProps(
      getExtensionGroups: GetExtensionGroups
   ): ExtensionGroupRegisterResults {
      return {
         endCursor: getExtensionGroups.extensionGroupsConnection.pageInfo.endCursor,
         hasNextPage: getExtensionGroups.extensionGroupsConnection.pageInfo.hasNextPage,
         items: getExtensionGroups.extensionGroupsConnection.items.map(this.convertExtensionGroupRegisterListItem),
         totalCount: getExtensionGroups.extensionGroupsConnection.totalCount,
      };
   }

   private convertExtensionGroupRegisterListItem(
      getExtensionGroupItem: GetExtensionGroups_extensionGroupsConnection_items
   ): ExtensionGroupRegisterListItem {
      return {
         extensionGroupId: getExtensionGroupItem.extensionGroupId,
         name: getExtensionGroupItem.name,
         description: getExtensionGroupItem.description,
         createDate: getExtensionGroupItem.createDate,
         extensionGroupExtensionCount: getExtensionGroupItem.extensionGroupExtensionsConnection.items.length,
      };
   }

   private loadNewData(
      filters: ExtensionGroupsFilterOption[],
      quickFilters: ExtensionGroupRegisterOptions | null = null,
      orderingOptions: ExtensionGroupOrderingOptions[] | null = null
   ) {
      return this.extensionGroupRegisterApiService
         .getExtensionGroups(null, filters, quickFilters, orderingOptions)
         .pipe(
            map((result) => {
               return extensionGroupRegisterAdapterActions.loadSuccess(
                  this.convertGetExtensionGroupsResultToLoadSuccessProps(result.data)
               );
            }),
            catchError((error) => {
               return of(extensionGroupRegisterAdapterActions.loadFailure(error));
            })
         );
   }

   private loadNewPage(
      endCursor: string | null,
      filters: ExtensionGroupsFilterOption[],
      quickFilters: ExtensionGroupRegisterOptions | null = null,
      orderingOptions: ExtensionGroupOrderingOptions[] | null = null
   ) {
      return this.extensionGroupRegisterApiService
         .getExtensionGroups(endCursor, filters, quickFilters, orderingOptions)
         .pipe(
            map((result) => {
               return extensionGroupRegisterAdapterActions.loadPageSuccess(
                  this.convertGetExtensionGroupsResultToLoadSuccessProps(result.data)
               );
            }),
            catchError((error) => {
               return of(extensionGroupRegisterAdapterActions.loadPageFailure(error));
            })
         );
   }
}
